import {
  Button,
  Form,
  Input,
  Radio,
  Select,
  Col,
  Space,
  Row,
  Table,
  DatePicker,
  Tooltip as ToolTip,
  Modal,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setScreenHeader } from "shared/redux/common-reducer";
import { RootState } from "shared/redux/store";
import { getEnumList, getEnumValue, INRFormatter, getEnumKey, getUser } from "shared/services/Utility";
import type { ColumnsType } from "antd/es/table";
import { ReactComponent as PlusOutlined } from "../../../src/assets/images/add_round.svg";
import {ReactComponent as IconInfo} from "../../assets/images/info_black.svg"
import OIButton from "shared/ComponentsOI/OIButton/OIButton";
import "../../../src/App.scss";
import "./PayoutSetting.scss";
import moment from "moment";
import { RangePickerProps } from "antd/lib/date-picker";
import API_SERVICE from "shared/services/api-service";
import { useParams } from "react-router-dom";


type Props = {
  isFromConnector: boolean;
  connector?: any;
  defaultPayoutConfigs?: any[];
};

function formatSlabRange(
  min: number | string | null | undefined,
  max: number | string | null | undefined,
  INRFormatterFn: (val: any, showSymbol?: boolean) => string = INRFormatter
): string {
  if ((min == null || min === "") && (max != null && max !== "")) {
    return `${INRFormatterFn(0, true)} - ${INRFormatterFn(max, true)}`;
  } else if ((min != null && min !== "") && (max == null || max === "" || max === 0)) {
    return `> ${INRFormatterFn(min, true)}`;
  } else if ((min != null && min !== "") && (max != null && max !== "")) {
    if (Number(min) === 0 && Number(max) === 0) {
      return "-";
    } else {
      return `${INRFormatterFn(min, true)} - ${INRFormatterFn(max, true)}`;
    }
  }
  return "-";
}

// Helper function to get payout config list
const fetchPayoutConfigList = async (isFromConnector: boolean, connectorId: string, tenantId: string, setLoadingTable?: (loading: boolean) => void) => {
  const params = {
    id: isFromConnector ? connectorId : tenantId,
    forConnector: !!isFromConnector,
    isLatest: true, // Use true for normal listing
    status: "ACTIVE",
  };
  if (setLoadingTable) setLoadingTable(true);
  try {
    const { data } = await API_SERVICE.getPayoutConfigList(params);
    return data;
  } finally {
    if (setLoadingTable) setLoadingTable(false);
  }
};

const PayoutSetting: React.FunctionComponent<Props> = ({ isFromConnector, connector, defaultPayoutConfigs = [] }) => {
  const dispatch = useDispatch();
  const [invoiceForm] = Form.useForm();
  const { appColors }: any = useSelector((state: RootState) => state.common);

  const { id } = useParams() as any;
  
  const connectorId = connector?.basicDetails?.connectorId || id;
  const tenantId = getUser()?.businessId;

  const [payoutType, setPayoutType] = useState<string>("");
  const [payout, setPayout] = useState<string>("");
  const [payoutStructure, setPayoutStructure] = useState<string>("");
  const [slabs, setSlabs] = useState([
    { min: "", max: "", payoutAs: "", amount: "", percent: "" },
  ]);
  const [slabErrors, setSlabErrors] = useState<any[]>([]);
  const [payoutTableData, setPayoutTableData] = useState<any[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingLender, setLoadingLender] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [minEffectiveDate, setMinEffectiveDate] = useState<moment.Moment | null>(null);
  const [allLenders, setAllLenders] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<any>(null);

const slabToolTip = 
<>
Each slab range must be incremental —<br/>the minimum value of a new slab should be greater than the maximum value of the previous slab.
<br/>Example:<br/>
<ul>
<li>Slab 1: ₹0 – ₹50,000 → 2% payout</li>
<li>Slab 2: ₹50,001 – ₹1,00,000 → 3% payout</li>
<li>Slab 3: ₹1,00,001 – ₹2,00,000 → 4% payout</li>
</ul>
<br/>Incorrect or overlapping slabs will cause configuration errors.
</>

  useEffect(() => {
    if (!isFromConnector) {
      dispatch(setScreenHeader({ screenTitle: "Payout Setting" }));
    }
  }, [dispatch, isFromConnector]);

  useEffect(() => {
    fetchPayoutConfigList(isFromConnector, connectorId, tenantId, setLoadingTable)
      .then((data) => {
        const list = data?.payload ?? [];
        setPayoutTableData(
          list.map((item, idx) => ({
            key: [item.tenantId, item.loanProductId, item.effectiveFrom].every(Boolean)
              ? `${item.tenantId}-${item.loanProductId}-${item.effectiveFrom}`
              : `row-${idx}`,
            lenderBusinessName: item.lenderBusinessName,
            productName: item.productName,
            effectiveDate: item.effectiveFrom || null,
            slabsrange: formatSlabRange(item.minAmount, item.maxAmount),
            payoutAs: (item.payoutAs && item?.payoutSubType && item?.payoutSubType.toUpperCase() === "AMOUNT")
              ? `${INRFormatter(item.payoutAs, true)}`
              : (item.payoutAs && item?.payoutSubType && item?.payoutSubType.toUpperCase() === "PERCENTAGE")
              ? `${item.payoutAs}%`
              : item.payoutAs ?? "-",
            payoutType: item.payoutType,
            payoutSubType: item.payoutSubType || "-",
            creationDate: item.creationDate,
          }))
        );
      });
  }, [isFromConnector, connectorId, tenantId]);

  const getAllLenders = (loanProductId) => {
    setLoadingLender(true);
    API_SERVICE.getLenders({
      status: "Active",
      loanProductId: loanProductId,
      pageNo: 1,
      pageSize: 200,
    })
    .then(({ data }) => {
      let lenders = data.payload.content.map((lender) => ({
        value: lender.lenderInfoId,
        label: lender.lenderName,
      }));
      setAllLenders(lenders);
    })
    .catch((e: any) => notification.error({ message: API_SERVICE.handleErrors(e) }))
    .finally(() => setLoadingLender(false));
  };

  const fetchMinEffectiveDate = async () => {
    if (isFromConnector && selectedProduct) {
      const loanProductId = getEnumKey("LOAN_TYPE", selectedProduct);
      API_SERVICE.getValidEffectiveDate({
        forConnector: true,
        loanProductId,
        id: connectorId,
        status: "ACTIVE",
      })
        .then(({ data }) => {
          if (data?.payload) {
            setMinEffectiveDate(moment(data.payload));
          } else {
            setMinEffectiveDate(null);
          }
        })
        .catch(() => setMinEffectiveDate(null));
    } else {
      setMinEffectiveDate(null);
    }
  }
  // Modify product selection effect to merge with defaultPayoutConfigs handling
  useEffect(() => {
    if (isFromConnector && selectedProduct && defaultPayoutConfigs?.length > 0) {
      // Find configs for selected product
      const productConfigs = defaultPayoutConfigs.filter(
        config => config.loanProductId === selectedProduct
      );
      if (productConfigs.length > 0) {
        // Use the first config for payout type/structure
        const firstConfig = productConfigs[0];
        const payoutTypeValue = firstConfig.payoutType?.toUpperCase();
        setPayout(payoutTypeValue);
        invoiceForm.setFieldsValue({ payoutType: payoutTypeValue });

        // Set payout structure if present
        if (firstConfig.payoutStructure) {
          const structureType = firstConfig.payoutStructure.replace(" ", "_")?.toUpperCase();
          setPayoutStructure(structureType);
          invoiceForm.setFieldsValue({ payoutStructure: structureType });
        } else {
          setPayoutStructure("");
          invoiceForm.setFieldsValue({ payoutStructure: undefined });
        }

        // Use only payoutSubType
        const subType = firstConfig.payoutSubType?.toUpperCase();
        setPayoutType(subType);
        invoiceForm.setFieldsValue({ payoutTypeDetail: subType });
        // For fixed, set percent or amount
        if (subType === "PERCENTAGE") {
          invoiceForm.setFieldsValue({ percent: firstConfig.payoutAs });
        } else if (subType === "AMOUNT") {
          invoiceForm.setFieldsValue({ rupees: firstConfig.payoutAs });
        }

        // Variable payout with slab based
        if (payoutTypeValue === "VARIABLE" && firstConfig.payoutStructure?.toUpperCase() === "SLAB BASED") {
          // Group all configs for this product as slabs
          const slabs = productConfigs.map(cfg => ({
            min: cfg.minAmount !== null && cfg.minAmount !== undefined ? cfg.minAmount.toString() : "",
            max: cfg.maxAmount !== null && cfg.maxAmount !== undefined ? cfg.maxAmount.toString() : "",
            payoutAs: cfg.payoutSubType === "PERCENTAGE" ? "PERCENTAGE_OF_DISBURSEMENT" : "FIXED_AMOUNT_PER_CASE",
            amount: cfg.payoutAs !== null && cfg.payoutAs !== undefined ? cfg.payoutAs.toString() : "",
            percent: cfg.payoutAs !== null && cfg.payoutAs !== undefined ? cfg.payoutAs.toString() : "",
          }));
          setSlabs(slabs.length ? slabs : [{ min: "", max: "", payoutAs: "", amount: "", percent: "" }]);
        }
      }
    }
  }, [selectedProduct, defaultPayoutConfigs, isFromConnector, invoiceForm]);

  const validateSlabs = (slabsToValidate = slabs, validateLastSlabMax = true) => {
    const errors = slabsToValidate.map((item, idx) => {
      const isLast = idx === slabsToValidate.length - 1;
      const err: any = {};
      if (!item.min) err.min = "Please Enter Amount";
      if (!isLast && !item.max) err.max = "Please Enter Amount";
      if (isLast && item.max && validateLastSlabMax) err.max = <>Last slab should not<br/>have a max amount</>;
      if (!item.payoutAs) err.payoutAs = "Required";
      if (item.payoutAs === "FIXED_AMOUNT_PER_CASE" && !item.amount) err.amount = "Please Enter Amount";
      if (item.payoutAs === "PERCENTAGE_OF_DISBURSEMENT" && !item.percent) err.percent = "Please Enter Percentage";
      // Incremental validation: min must be exactly 1 greater than previous max
      if (idx > 0) {
        const prevMax = Number(slabsToValidate[idx - 1].max);
        const currMin = Number(item.min);
        if (!isNaN(prevMax) && !isNaN(currMin) && currMin !== prevMax + 1) {
          err.min = "Enter a Valid Amount";
        }
      }
      if ((Number(item.max) <= Number(item.min)) && !isLast) {
        err.max = <>Max should be greater <br/> than Min</>;
      } 
      return err;
    });
    setSlabErrors(errors);
    return errors.every(e => Object.keys(e).length === 0);
  };

  const handleFormSave = async () => {
    try {
      const formValues = await invoiceForm.validateFields();
      // Only validate slabs if payoutStructure is SLAB_BASED
      if (payoutStructure === "SLAB_BASED" && !validateSlabs(slabs, true)) return;

      const _tenantId = tenantId;
      const _connectorId = connectorId;
      const loanProduct = formValues.product;
      // Format effectiveFrom as 'YYYY-MM-DD' to avoid timezone issues
      let effectiveFrom = formValues.effectiveDate
        ? moment(formValues.effectiveDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");

      let payload: any = {
        tenantId: isFromConnector ? undefined : _tenantId,
        connectorId: isFromConnector ? _connectorId : undefined,
        loanProduct,
        effectiveFrom,
        lenderBusinessId: formValues.lenderBusinessId
      };

      // Use mapped values for payload
      payload.payoutType = payout;
      if (payout === "VARIABLE") {
        payload.payoutStructure = payoutStructure;
        if (payoutStructure === "CASE_LEVEL") {
          payload.payoutSubType = payoutType;
          if (payoutType === "AMOUNT") {
            payload.payoutAmount = Number(formValues.rupees);
          } else if (payoutType === "PERCENTAGE") {
            payload.payoutPercentage = Number(formValues.percent);
          }
        } else if (payoutStructure === "SLAB_BASED") {
          payload.payoutSlabs = slabs.map((slab, idx) => {
            const isLast = idx === slabs.length - 1;
            return {
              minAmount: Number(slab.min),
              maxAmount: !isLast && slab.max !== '' ? Number(slab.max) : null,
              subType: slab.payoutAs === "FIXED_AMOUNT_PER_CASE" ? "AMOUNT" : "PERCENTAGE",
              value:
                slab.payoutAs === "FIXED_AMOUNT_PER_CASE"
                  ? Number(slab.amount)
                  : Number(slab.percent),
            };
          });
        }
      } else if (payout === "FIXED") {
        payload.payoutSubType = payoutType;
        if (payoutType === "AMOUNT") {
          payload.payoutAmount = Number(formValues.rupees);
        } else if (payoutType === "PERCENTAGE") {
          payload.payoutPercentage = Number(formValues.percent);
        }
      }

      const data = await fetchPayoutConfigList(isFromConnector, connectorId, tenantId);
      const loanProductId = getEnumKey("LOAN_TYPE", loanProduct);
      const existingData = data?.payload?.filter(
        (item: any) => item.loanProductId === loanProductId
      ) || [];
      if (existingData.length > 0 && payoutStructure === "SLAB_BASED") {
        setConfirmModalData({
          slabs: payoutStructure === "SLAB_BASED" ? slabs.map((slab) => ({
            minAmount: Number(slab.min),
            maxAmount: Number(slab.max),
            percentage: slab.payoutAs === "PERCENTAGE_OF_DISBURSEMENT" ? Number(slab.percent) : undefined,
            amount: slab.payoutAs === "FIXED_AMOUNT_PER_CASE" ? Number(slab.amount) : undefined,
            payoutAs: slab.payoutAs,
          })) : [],
          effectiveFrom: formValues.effectiveDate,
          userPayload: payload, // Store user-entered payload for saving
        });
        setShowConfirmModal(true);
      } else {
        await savePayoutConfig(payload);
      }
    } catch (error: any) {
      notification.error({ message: API_SERVICE.handleErrors(error) });
    }
  };

  const savePayoutConfig = async (payload: any) => {
    try {
      await API_SERVICE.savePayoutConfig(payload);
      notification.success({ message: "Payout configuration saved successfully" });
      // Refresh table data
      const data = await fetchPayoutConfigList(isFromConnector, connectorId, tenantId);
      const list = data?.payload ?? [];
      setPayoutTableData(
        list.map((item, idx) => ({
          key: [item.tenantId, item.loanProductId, item.effectiveFrom].every(Boolean)
            ? `${item.tenantId}-${item.loanProductId}-${item.effectiveFrom}`
            : `row-${idx}`,
          lenderBusinessName: item.lenderBusinessName,
          productName: item.productName,
          effectiveDate: item.effectiveFrom || null,
          slabsrange: formatSlabRange(item.minAmount, item.maxAmount),
          payoutAs: (item.payoutAs && item?.payoutSubType && item?.payoutSubType.toUpperCase() === "AMOUNT")
            ? `${INRFormatter(item.payoutAs, true)}`
            : (item.payoutAs && item?.payoutSubType && item?.payoutSubType.toUpperCase() === "PERCENTAGE")
            ? `${item.payoutAs}%`
            : item.payoutAs ?? "-",
          payoutType: item.payoutType,
          payoutSubType: item.payoutSubType || "-",
          creationDate: item.creationDate,
        }))
      );
    } catch (error: any) {
      notification.error({ message: API_SERVICE.handleErrors(error) });
    }
  };

  const confirmSave = async () => {
    try {
      setShowConfirmModal(false);
      const payload = confirmModalData.userPayload;
      await savePayoutConfig(payload);
    } catch (error: any) {
      notification.error({ message: API_SERVICE.handleErrors(error) });
    }
  };

  const handleAddRow = () => {
    if (!validateSlabs(slabs, false)) return;
    setSlabs([
      ...slabs,
      { min: "", max: "", payoutAs: "", amount: "", percent: "" },
    ]);
    setSlabErrors([...slabErrors, {}]);
  };

  const handleSlabFieldChange = (index: number, field: string, value: string) => {
    const newSlabs = [...slabs];
    if (field === "percent") {
      const numValue = Number(value);
      if (numValue > 100) return;
    }
    newSlabs[index][field] = value;
    setSlabs(newSlabs);
    const newErrors = [...slabErrors];

    if (newErrors[index]) {
      delete newErrors[index][field];
      setSlabErrors(newErrors);
    }

    if (!newErrors[index]) {
      newErrors[index] = {};
    }
  
    const min = Number(newSlabs[index].min);
    const max = Number(newSlabs[index].max);
  
    if (field === "max") {
      if (!isNaN(min) && !isNaN(max)) {
        if (max <= min ) {
          newErrors[index].max = <>Max should be greater <br/> than Min</>;
        } else {
          delete newErrors[index].max;
        }
      } else {
        delete newErrors[index].max;
      }
    }
  
    if (newErrors[index] && Object.keys(newErrors[index]).length === 0) {
      newErrors[index] = {};
    }
  
    setSlabErrors(newErrors);

  };

  const columns: ColumnsType<any> = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      sorter: (a, b) => {
        const aVal = (a.productName || '').toString();
        const bVal = (b.productName || '').toString();
        if (!aVal && !bVal) return 0;
        if (!aVal) return -1;
        if (!bVal) return 1;
        return aVal.localeCompare(bVal);
      },
      sortDirections:['ascend', 'descend'],
      showSorterTooltip: false
    },
    {
      title: "Lender Name",
      dataIndex: "lenderBusinessName",
      key: "lenderBusinessName",
      sorter: (a, b) => {
        const aVal = (a.lenderBusinessName || '').toString();
        const bVal = (b.lenderBusinessName || '').toString();
        if (!aVal && !bVal) return 0;
        if (!aVal) return -1;
        if (!bVal) return 1;
        return aVal.localeCompare(bVal);
      },
      sortDirections:['ascend', 'descend'],
      showSorterTooltip: false
    },
    ...(isFromConnector
      ? [
          {
            title: "Effective Date",
            dataIndex: "effectiveDate",
            key: "effectiveDate",
            sorter: (a, b) => {
              const aDate = a.effectiveDate ? moment(a.effectiveDate, "DD-MM-YYYY").valueOf() : 0;
              const bDate = b.effectiveDate ? moment(b.effectiveDate, "DD-MM-YYYY").valueOf() : 0;
              return aDate - bDate;
            },
            showSorterTooltip: false
          },
        ]
      : []),
    {
      title: "Slabs (Range)",
      dataIndex: "slabsrange",
      key: "slabsrange",
    },
    {
      title: "Payout",
      dataIndex: "payoutAs",
      key: "payoutAs",
    },
    {
      title: "Payout Type",
      dataIndex: "payoutType",
      key: "payoutType",
      sorter: (a, b) => {
        const aVal = (a.payoutType || '').toString();
        const bVal = (b.payoutType || '').toString();
        if (!aVal && !bVal) return 0;
        if (!aVal) return -1;
        if (!bVal) return 1;
        return aVal.localeCompare(bVal);
      },
      sortDirections:['ascend', 'descend'],
      showSorterTooltip: false
    },
    {
      title: "Payout Subtype",
      dataIndex: "payoutSubType",
      key: "payoutSubType",
    },
    {
      title: "Configured Date",
      dataIndex: "creationDate",
      key: "creationDate",
      sorter: (a, b) => {
        const aDate = a.creationDate ? moment(a.creationDate, "DD-MM-YYYY").valueOf() : 0;
        const bDate = b.creationDate ? moment(b.creationDate, "DD-MM-YYYY").valueOf() : 0;
        return aDate - bDate;
      },
      sortDirections:['ascend', 'descend'],
      showSorterTooltip: false
    }
  ];

  const dateFormat = "DD-MM-YYYY";
  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > moment().endOf("day");
  };

  const renderPayoutSlabForm = () => {
    return (
      <Form.Item
        label="Payout Slab Configuration *"
        tooltip={{ title: slabToolTip, icon: <IconInfo style={{ width: '15px' }} /> }}
        preserve={false}>
        
        <div className="payout-setting-flex">
          <table className="ant-table payout-setting-table">
            <thead className="ant-table-thead">
              <tr>
                <th>Min</th>
                <th>Max</th>
                <th>Payout As</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="payout-setting-slab-con-tbl-body">
              {slabs.map((item, index) => {
                const errors = slabErrors[index] || {};
                const isLast = index === slabs.length - 1;
                return (
                  <tr key={index}>
                    <td style={{ verticalAlign: 'top', minWidth: 120 }}>
                      <Input
                        placeholder="Min"
                        value={item.min}
                        onChange={e => handleSlabFieldChange(index, "min", e.target.value)}
                        status={errors.min ? 'error' : ''}
                        style={errors.min ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px #fff1f0' } : {}}
                        type="number"
                        min={0}
                      />
                      {errors.min && <div style={{ color: '#ff4d4f', fontSize: 12 }}>{errors.min}</div>}
                    </td>
                    <td style={{ verticalAlign: 'top', minWidth: 120 }}>
                      <Input
                        placeholder="Max"
                        value={item.max}
                        onChange={e => handleSlabFieldChange(index, "max", e.target.value)}
                        status={errors.max ? 'error' : ''}
                        style={errors.max ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px #fff1f0' } : {}}
                        type="number"
                        min={0}
                        disabled={isLast} // Disable max input for last row only
                      />
                      {errors.max && <div style={{ color: '#ff4d4f', fontSize: 12 }}>{errors.max}</div>}
                    </td>
                    <td style={{ verticalAlign: 'top', minWidth: 170 }}>
                      <Select
                        placeholder="Select Payout As"
                        value={item.payoutAs || undefined}
                        onChange={value => handleSlabFieldChange(index, "payoutAs", value)}
                        style={errors.payoutAs ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px #fff1f0', width: '150px' } : { width: '150px' }}
                        status={errors.payoutAs ? 'error' : ''}
                      >
                        <Select.Option value="FIXED_AMOUNT_PER_CASE">
                          Fixed Amount Per Case (₹)
                        </Select.Option>
                        <Select.Option value="PERCENTAGE_OF_DISBURSEMENT">
                          Percentage of Disbursement %
                        </Select.Option>
                      </Select>
                      {errors.payoutAs && <div style={{ color: '#ff4d4f', fontSize: 12 }}>{errors.payoutAs}</div>}
                    </td>
                    {item.payoutAs && (
                      <td style={{ verticalAlign: 'top', minWidth: 170 }}>
                        {item.payoutAs === "FIXED_AMOUNT_PER_CASE" && (
                          <Input
                            type="number"
                            placeholder="₹"
                            value={item.amount || ""}
                            onChange={e => handleSlabFieldChange(index, "amount", e.target.value)}
                            status={errors.amount ? 'error' : ''}
                            style={errors.amount ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px #fff1f0' } : {}}
                            addonBefore={<span style={{ fontWeight: 600, background: 'transparent' }}>₹</span>}
                          />
                        )}
                        {item.payoutAs === "PERCENTAGE_OF_DISBURSEMENT" && (
                          <Input
                            placeholder="%"
                            style={{ textAlign: "right", ...(errors.percent ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px #fff1f0' } : {}) }}
                            value={item.percent || ""}
                            onChange={e => handleSlabFieldChange(index, "percent", e.target.value)}
                            status={errors.percent ? 'error' : ''}
                            addonAfter={<span style={{ fontWeight: 600 }}>%</span>}
                            type="number"
                            min={1}
                            max={100}
                            step="any"
                          />
                        )}
                        {(errors.amount || errors.percent) && <div style={{ color: '#ff4d4f', fontSize: 12 }}>{errors.amount || errors.percent}</div>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Button
            type="text"
            className="payout-setting-add-btn"
            onClick={handleAddRow}
            icon={<PlusOutlined />}
          />
        </div>
      </Form.Item>
    );
  };
  const renderDefaultPayouts = () => {
    if (!payoutTableData.length) return null;
    return (
      <>
        <h6>{isFromConnector ? "Effective Payouts" : "Default Payouts"}</h6>
        <Table
          className="dynamic-pagination-checkbox"
          style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
          scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={payoutTableData}
          loading={loadingTable}
          size="small"
          pagination={{}}
          rowKey="key"
        />
      </>
    );
  };

  const renderConfirmationModal = () => (
    <Modal
      visible={showConfirmModal}
      onCancel={() => setShowConfirmModal(false)}
      footer={null}
      centered
    >
      <div style={{ textAlign: "center", padding: "20px" }}>
        <p>A payout is already configured for this product. Do you want to override it with the new configuration?</p>
        <div className="payout-setting-confirm-modal-card">
          <div className="payout-setting-confirm-modal-title">
            Variable Payout – Slab Based
          </div>
          <div className="payout-setting-confirm-modal-grid">
            {confirmModalData?.slabs?.map((slab, index) => (
              <React.Fragment key={index}>
                <div className={`payout-setting-confirm-modal-cell payout-setting-confirm-modal-range${index !== confirmModalData.slabs.length - 1 ? ' payout-setting-confirm-modal-cell-border' : ''}`}>{formatSlabRange(slab.minAmount, slab.maxAmount)}</div>
                <div className={`payout-setting-confirm-modal-cell payout-setting-confirm-modal-value${index !== confirmModalData.slabs.length - 1 ? ' payout-setting-confirm-modal-cell-border' : ''}`}>{slab.payoutAs === "PERCENTAGE_OF_DISBURSEMENT" ? `${slab.percent || slab.percentage}%` : `₹ ${slab.amount}`}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
        {isFromConnector && (
          <p>Effective from {confirmModalData?.effectiveFrom ? moment(confirmModalData.effectiveFrom).format("DD-MM-YYYY") : ""}</p>
        )}
        <p>* Updating the payout configuration will not affect already disbursed cases.<br/>For such cases, manual update is required.</p>
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <OIButton label="Confirm" type="primary" style={
            { "--custColor": appColors?.appPrimaryColor } as any
          } onClick={confirmSave} />
          <OIButton label="Cancel" style={
            { "--custColor": appColors?.appSecondaryColor } as any
          } onClick={() => setShowConfirmModal(false)} />
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      <div className="content-box" style={{marginInline: 0}}>
        <Form layout="vertical" form={invoiceForm} onFinish={handleFormSave}>
          <div>
            <Row gutter={[24, 24]}>
              <Col xs={24} xl={12} span={12}>
                <div className="form-box">
                  <Form.Item
                    className="field-bg"
                    label="Product"
                    name="product"
                    rules={[
                      {
                        required: true,
                        message: "Please select Product",
                      },
                    ]}
                    style={{ marginBottom: "5px", marginTop: "5px" }}
                  >
                    <Select
                      placeholder="Select Product"
                      className="custom-select"
                      onChange={(value) => {
                        getAllLenders(value);
                        setSelectedProduct(value);
                        invoiceForm.resetFields(); // Reset all form fields
                        invoiceForm.setFieldsValue({ product: value }); // Set product field
                        setPayout("");
                        setPayoutType("");
                        setPayoutStructure("");
                        setSlabs([
                          { min: "", max: "", payoutAs: "", amount: "", percent: "" },
                        ]);
                      }}
                      value={selectedProduct || undefined}
                    >
                      {getEnumList("LOAN_TYPE").map(
                        (option: any, i: number) => (
                          <Select.Option key={i} value={option.key}>
                            {option.value}
                          </Select.Option>
                        )
                      )}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    className="field-bg"
                    label="Select Lender"
                    name="lenderBusinessId"
                    style={{ marginBottom: "5px", marginTop: "5px" }}
                    tooltip={{
                      title: (
                        <ul>
                          <li>Select a lender if you want to define a specific payout for that lender and product combination.</li>
                          <li>If no lender is selected, the default payout set for the chosen product will apply across all lenders</li>
                        </ul>
                      ), 
                      icon: <IconInfo style={{ width: '15px' }} /> 
                    }}
                  >
                    <Select
                      placeholder="Select Lender"
                      className="custom-select"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      // onSearch={debounce((value) => getAllLenders(value), 1000)}
                      options={allLenders}
                      disabled={!selectedProduct}
                      loading={loadingLender}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Choose Payout"
                    name="payoutType"
                    style={{ marginBottom: "5px", marginTop: "5px" }}
                    rules={[{ required: true, message: "Please select payout" }]}
                  >
                    <Radio.Group
                      onChange={(e) => {
                        setPayout(e.target.value);
                        setPayoutStructure("");
                        setPayoutType("");
                        invoiceForm.setFieldsValue({ payoutStructure: undefined, payoutTypeDetail: undefined });
                      }}
                      disabled={!selectedProduct}
                      value={payout || undefined}
                    >
                      <Radio value="VARIABLE">Variable Payout</Radio>
                      <Radio value="FIXED">Fixed Payout</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {payout === "VARIABLE" && (
                    <Form.Item
                      label="Choose Variable Payout Structure"
                      name="payoutStructure"
                      style={{ marginBottom: "5px", marginTop: "5px" }}
                      rules={[{ required: true, message: "Please select payout structure." }]}
                    >
                      <Radio.Group
                        onChange={(e) => {
                          setPayoutStructure(e.target.value);
                          setPayoutType("");
                        }}
                        disabled={payout !== "VARIABLE"}
                        value={payoutStructure || undefined}
                      >
                        <Radio value="CASE_LEVEL">Case Level</Radio>
                        <Radio value="SLAB_BASED">Slab-Based</Radio>
                      </Radio.Group>
                    </Form.Item>
                  )}

                  {payoutStructure === "SLAB_BASED" && renderPayoutSlabForm()}

                  {payoutStructure !== "SLAB_BASED" && (
                    <Form.Item
                      label="Choose Payout Type"
                      name="payoutTypeDetail"
                      style={{ marginBottom: "5px", marginTop: "5px" }}
                      rules={[{ required: true, message: "Please select payout type." }]}
                    >
                      <Radio.Group
                        onChange={(e) => setPayoutType(e.target.value)}
                        disabled={!payout || (payout === "VARIABLE" && !payoutStructure)}
                        value={payoutType || undefined}
                      >
                        <Radio value="AMOUNT">Fixed Amount Per Case (₹)</Radio>
                        <Radio value="PERCENTAGE">Percentage of Disbursement %</Radio>
                      </Radio.Group>
                    </Form.Item>
                  )}

                  {payoutStructure !== "SLAB_BASED" && payoutStructure !== "CASE_LEVEL" && payoutType === "AMOUNT" && (
                    <Form.Item
                      label=""
                      name="rupees"
                      preserve={false}
                      rules={[
                        { required: true, message: "Please enter amount." },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || Number.isInteger(Number(value))) {
                              return Promise.resolve();
                            }
                            return Promise.reject("Amount must be a number.");
                          },
                        }),
                      ]}
                    >
                      <Input
                        className="custom-input"
                        type="number"
                        min={0}
                        step="any"
                        placeholder="₹"
                        style={{ width: "45%", display: "block", marginTop: "5px" }}
                      />
                    </Form.Item>
                  )}

                  {payoutStructure !== "SLAB_BASED" && payoutStructure !== "CASE_LEVEL" && payoutType === "PERCENTAGE" && (
                    <Form.Item
                      label=""
                      name="percent"
                      preserve={false}
                      rules={[
                        { required: true, message: "Please enter percent." },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              value === undefined || value === "" ||
                              (!isNaN(value) && value >= 0 && value <= 100)
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              "Percentage must be a number between 0 and 100."
                            );
                          },
                        }),
                      ]}
                    >
                      <Input
                        type="number"
                        className="custom-input"
                        placeholder="%"
                        min={1}
                        max={100}
                        step="any"
                        style={{ width: "45%", display: "block" }}
                      />
                    </Form.Item>
                  )}
                  {isFromConnector && (
                    <>
                      <Form.Item
                        name="effectiveDate"
                        label="Effective From"
                        tooltip={{ title: <>
                          Only dates on or after the last <br />
                          Effective From date are allowed <br />
                          to avoid backdating payouts.
                        </>, icon: <IconInfo style={{ width: '15px' }} /> }}
                        rules={[{ required: true, message: "Please select an effective date." }]}
                        style={{ marginTop: "10px" }}
                      >
                        
                        <DatePicker
                          format={dateFormat}
                          className="custom-input payout-setting-date"
                          placeholder="Select Date"
                          disabled={
                            !selectedProduct ||
                            !payout ||
                            (payout === "VARIABLE" && !payoutStructure) ||
                            (payoutStructure !== "SLAB_BASED" && !payoutType)
                          }
                          disabledDate={(current) => {
                            if (minEffectiveDate) {
                              return current && current < minEffectiveDate.startOf("day");
                            }
                            return false
                          }}
                          onFocus={fetchMinEffectiveDate}
                          style={{ width: "45%", display: "block", marginTop: "5px" }}
                          onChange={(date) => invoiceForm.setFieldsValue({ effectiveDate: date })}
                        />
                      </Form.Item>
                    </>
                  )}

                  <Space style={{ marginTop: "20px" }}>
                    <Form.Item>
                      <OIButton
                        label="Save"
                        type="primary"
                        htmlType="submit"
                        style={
                          { "--custColor": appColors?.appPrimaryColor } as any
                        }
                      />
                    </Form.Item>
                    <Form.Item>
                      <OIButton
                        label="Cancel"
                        style={
                          { "--custColor": appColors?.appSecondaryColor } as any
                        }
                        onClick={() => {
                          invoiceForm.resetFields();
                          setSelectedProduct("")
                          setPayout("");
                          setPayoutType("");
                          setPayoutStructure("");
                          setSlabs([
                            {
                              min: "",
                              max: "",
                              payoutAs: "",
                              amount: "",
                              percent: "",
                            },
                          ]);
                        }}
                      />
                    </Form.Item>
                  </Space>
                </div>
              </Col>
              <Col xs={24} xl={12} span={12} style={{paddingLeft: 0}}>
                {renderDefaultPayouts()}
              </Col>
            </Row>
          </div>
        </Form>
      </div>
      {renderConfirmationModal()}
    </>
  );
};

export default PayoutSetting;
